/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { getDocumentServicesTable, getVatGroupSums, reprintDocument, setVatGroupSums } from "@/requests/document";
import { VatGroupSumWithUnmodified } from "@/models/api/documents/VatGroupSum";
import { DocumentServiceItem } from "@/models/api/documents/DocumentServicesList";
import BigNumber from "bignumber.js";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface DocumentsVatTableStateInterface {
  documentId: string | null;
  vatTable: Array<VatGroupSumWithUnmodified>;
  documentItems: Array<DocumentServiceItem>;
}

export const DocumentsVatTableState: DocumentsVatTableStateInterface = {
  documentId: null,
  vatTable: [],
  documentItems: [],
};

const DocumentsVatTableModule = defineModule({
  state: DocumentsVatTableState as DocumentsVatTableStateInterface,
  namespaced: true as const,
  getters: {
    documentNetValue(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const table of state.vatTable) {
        sum = sum.plus(table.unmodifiedNet);
      }
      return sum.toNumber();
    },
    documentVatAmount(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const table of state.vatTable) {
        sum = sum.plus(table.unmodifiedVatAmount);
      }
      return sum.toNumber();
    },
    documentGross(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const table of state.vatTable) {
        sum = sum.plus(table.unmodifiedGross);
      }
      return sum.toNumber();
    },
    vatSectionNetValue(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const vatGroupSum of state.vatTable) {
        sum = sum.plus(vatGroupSum.net);
      }
      return sum.toNumber();
    },
    vatSectionNetVatAmount(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const vatGroupSum of state.vatTable) {
        sum = sum.plus(vatGroupSum.vatAmount);
      }
      return sum.toNumber();
    },
    vatSectionGross(...args): number {
      const { state } = _moduleGetterContext(args); // eslint-disable-line
      let sum = new BigNumber(0);
      for (const vatGroupSum of state.vatTable) {
        sum = sum.plus(vatGroupSum.gross);
      }
      return sum.toNumber();
    },
  },
  mutations: {
    setDocumentId(state, param: string | null) {
      state.documentId = param;
    },
    setVatTable(state, param: Array<VatGroupSumWithUnmodified>) {
      state.vatTable = param;
    },
    setDocumentItems(state, param: Array<DocumentServiceItem>) {
      state.documentItems = param;
    },
    recalculateVatItem(state, id: number) {
      const index = state.vatTable.findIndex((v) => v.id === id);
      if (typeof index !== "undefined") {
        if (state.vatTable[index].modifier < -0.99) {
          state.vatTable[index].modifier = -0.99;
        } else if (state.vatTable[index].modifier > 0.99) {
          state.vatTable[index].modifier = 0.99;
        }
        state.vatTable[index].vatAmount = new BigNumber(state.vatTable[index].unmodifiedVatAmount)
          .plus(state.vatTable[index].modifier)
          .minus(state.vatTable[index].unmodifiedModifier)
          .toNumber();
        state.vatTable[index].gross = new BigNumber(state.vatTable[index].unmodifiedGross)
          .plus(state.vatTable[index].modifier)
          .minus(state.vatTable[index].unmodifiedModifier)
          .toNumber();
      }
    },
  },
  actions: {
    async openModifyVatTableDialog(context, documentId: string) {
      const { commit } = _moduleActionContext(context);
      commit.setDocumentItems(await getDocumentServicesTable(documentId));
      const vatTable = await getVatGroupSums(documentId);
      commit.setVatTable(vatTable.map((v) => VatGroupSumWithUnmodified.fromVatGroupSum(v)));
      commit.setDocumentId(documentId);
    },
    async saveModifyVatTable(context): Promise<void> {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.documentId !== null) {
        await setVatGroupSums(
          state.documentId,
          state.vatTable.map((v) => VatGroupSumWithUnmodified.toVatGroupSum(v))
        );
        await rootDispatch.documents.getDocumentsTable();
        const isConfirmedReprintVatChange = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("vatChangeReprint")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                outlined: true,
                value: false,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "550px"
          )
        )) as boolean;
        if (isConfirmedReprintVatChange) {
          await reprintDocument(
            {
              documentId: state.documentId,
              culture: "",
              layout: "",
            },
            false
          );
          rootDispatch.mails.openNotificationDialog([state.documentId]);
        }
      }
    },
  },
});

export default DocumentsVatTableModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, DocumentsVatTableModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, DocumentsVatTableModule);
